import { useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { MapPin, Phone, Clock, Car, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, GoldRule, Photo } from "@/components/Shared";
import {
  HairHero, HairBand, HairEyebrow, HairHeading, HairCard, Bullets, HairFaq, PathwayStrip, CandidacyBand, PracticeStandards, HairCTABand, JumpNav, HairBookButton, scrollToAnchor,
} from "@/components/HairShared";
import { StickyCTA } from "@/components/Landing";
import { StageQuiz } from "@/components/NorwoodScale";
import BookingPanel from "@/components/adlanding/BookingPanel";
import { OFFERS } from "@/data/landing";
import { JOURNAL_POSTS, CLINIC, getTeamMember } from "@/data/content";
import { PHOTOS } from "@/data/photos";
import { HAIR_LANDING, HAIR_PILLARS, HAIR_JOURNAL_SLUGS, HAIR_HERO, HAIR_CREDENTIAL } from "@/data/hair";
import { useLandingVisit } from "@/lib/attribution";
import { trackEvent } from "@/lib/analytics";

const Anchor = ({ id }) => <div id={id} />;

const OptionCard = ({ card, index }) => (
  <Reveal delay={index * 0.08} className="h-full">
    <Link to={card.to} data-testid={`hair-option-${card.to.split("/").pop()}`} className="group block h-full bg-hair-panel border border-gold/20 hover:border-gold/60 transition-all duration-500 hover:-translate-y-1">
      <div className="aspect-[4/5] overflow-hidden">
        <img src={card.image.id} alt={card.alt} loading="lazy" decoding="async" width={900} height={1125} style={{ objectPosition: card.image.pos }} className="filmic w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
      </div>
      <div className="p-6 sm:p-7">
        <h3 className="text-warm-white text-2xl">{card.title}</h3>
        <p className="text-warm-white/65 text-sm mt-2 leading-relaxed">{card.text}</p>
        <span className="inline-flex items-center gap-2 mt-4 text-[11px] tracking-[0.18em] uppercase font-medium text-gold group-hover:text-hairline transition-colors duration-300">
          Explore <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </span>
      </div>
    </Link>
  </Reveal>
);

const ClinicFacts = () => (
  <ul className="space-y-5" data-testid="hair-clinic-facts">
    {[
      [MapPin, CLINIC.address, HAIR_LANDING.clinic.mapsUrl, "Open in Google Maps", "hair-clinic-map-link"],
      [Phone, CLINIC.phone, `tel:${CLINIC.phoneTel}`, "Call the practice", "hair-clinic-phone"],
      [Clock, CLINIC.hoursText],
      [Car, CLINIC.parking],
    ].map(([Icon, text, href, label, testId]) => (
      <li key={text} className="flex items-start gap-4">
        <Icon className="w-4 h-4 text-gold mt-1 shrink-0" strokeWidth={1.5} aria-hidden="true" />
        <div>
          <p className="text-ink/85 text-[15px] leading-relaxed">{text}</p>
          {href && (
            <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel={href.startsWith("http") ? "noreferrer" : undefined} data-testid={testId} className="link-sea text-[11px] tracking-[0.16em] uppercase font-medium">
              {label}
            </a>
          )}
        </div>
      </li>
    ))}
  </ul>
);

const HairLanding = () => {
  const L = HAIR_LANDING;
  const truong = getTeamMember("truong");
  const { hash } = useLocation();
  const posts = HAIR_JOURNAL_SLUGS.map((s) => JOURNAL_POSTS.find((p) => p.slug === s)).filter(Boolean);
  const { source, markInteracting } = useLandingVisit("/hair", "hair");

  useEffect(() => { trackEvent("hair_landing_view", { page: "/hair", source }); }, [source]);
  useEffect(() => {
    if (!hash) return undefined;
    const t = setTimeout(() => scrollToAnchor(hash.slice(1)), 160);
    return () => clearTimeout(t);
  }, [hash]);

  const onInteract = useCallback(() => {
    trackEvent("hair_booking_started", { page: "/hair", source, method: "widget" });
    markInteracting();
  }, [source, markInteracting]);
  const onCallback = useCallback(() => trackEvent("hair_callback_requested", { page: "/hair", source }), [source]);

  return (
    <>
      <SEO
        title={L.seoTitle}
        description={L.seoDescription}
        ogImage={HAIR_HERO.truong.id}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "FAQPage", mainEntity: L.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://www.lumiereaesthetics.com.au/" },
                { "@type": "ListItem", position: 2, name: "Hair", item: "https://www.lumiereaesthetics.com.au/hair" },
              ],
            },
          ],
        }}
      />

      <HairHero eyebrow={L.eyebrow} title={L.h1} standfirst={L.standfirst} offer={OFFERS.hair} bullets={L.bullets} image={HAIR_HERO.truong} portrait testId="hair-landing-hero">
        <button type="button" onClick={() => scrollToAnchor("treatments")} data-testid="hero-treatments-link" className="inline-flex items-center gap-2 text-warm-white text-[12px] tracking-[0.14em] uppercase font-medium border-b border-warm-white/50 pb-1 hover:border-gold hover:text-gold transition-colors duration-300">
          See treatment options ↓
        </button>
      </HairHero>

      <JumpNav items={L.jumpNav} />

      <Anchor id="treatments" />
      <HairBand testId="hair-treatment-options">
        <Reveal className="mb-14 max-w-3xl">
          <HairEyebrow className="mb-3">{L.options.eyebrow}</HairEyebrow>
          <HairHeading>{L.options.heading}</HairHeading>
          <GoldRule className="mt-6" />
          <p className="text-ink/80 leading-[1.8] mt-6 text-[17px]">{L.options.intro}</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="hair-options-grid">
          {L.options.cards.map((card, i) => <OptionCard key={card.to} card={card} index={i} />)}
        </div>
        <Reveal className="mt-12 flex flex-wrap items-center gap-5">
          <HairBookButton source="options" testId="options-book-button" className="btn-hair-gold px-8 py-4 text-sm" />
          <button type="button" onClick={() => scrollToAnchor("stages")} data-testid="options-stages-link" className="text-deep-sea hover:text-gold transition-colors duration-300 text-[12px] tracking-[0.16em] uppercase font-medium">Not sure where you are? Find your stage ↓</button>
        </Reveal>
      </HairBand>

      <Anchor id="why" />
      <HairBand sand testId="hair-why">
        <Reveal className="mb-14">
          <HairEyebrow className="mb-3">{L.why.eyebrow}</HairEyebrow>
          <HairHeading>{L.why.heading}</HairHeading>
          <GoldRule className="mt-6" />
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-10" data-testid="hair-pillars">
          {HAIR_PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.07}>
              <div className="border-t border-gold/60 pt-6 h-full" data-testid={`hair-pillar-${i}`}>
                <span className="font-serif italic text-gold text-2xl font-light">{i + 1}</span>
                <h3 className="text-ink mt-3">{p.title}</h3>
                <p className="text-ink/70 text-sm mt-3 leading-relaxed">{p.line}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </HairBand>

      <Anchor id="doctor" />
      <section className="hair-dark bg-hair-dark" data-testid="hair-doctor-quote">
        <div className="shell py-20 lg:py-28">
          <Reveal className="max-w-4xl">
            <HairEyebrow className="mb-6">Your doctor</HairEyebrow>
            <blockquote className="border-l-2 border-gold pl-8">
              <p className="font-serif italic font-light text-warm-white text-2xl lg:text-[32px] leading-[1.4]" data-testid="hair-quote-text">“{L.quote.text}”</p>
            </blockquote>
            <p className="font-serif text-warm-white text-2xl mt-8">{L.quote.name}</p>
            <p className="text-warm-white/60 text-[13px] mt-1">{L.quote.title}</p>
            <p className="text-warm-white/75 text-[15px] leading-relaxed mt-6 max-w-xl">{HAIR_CREDENTIAL}, Dr Truong assesses, plans and performs every hair restoration procedure at Lumière herself — and is the doctor you see at every review.</p>
            <Link to="/hair/dr-kristy-truong" data-testid="hair-quote-link" className="inline-flex items-center gap-2 mt-6 text-[12px] tracking-[0.16em] uppercase font-medium text-gold hover:text-hairline transition-colors duration-300">
              About Dr Truong <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      <Anchor id="stages" />
      <HairBand dark testId="hair-stage-finder" className="border-t border-gold/15">
        <Reveal className="mb-12 max-w-3xl">
          <HairEyebrow className="mb-3">{L.stages.eyebrow}</HairEyebrow>
          <HairHeading dark>{L.stages.heading}</HairHeading>
          <GoldRule className="mt-6" />
          <p className="text-warm-white/80 leading-[1.8] mt-6 text-[17px]">{L.stages.intro}</p>
        </Reveal>
        <StageQuiz />
      </HairBand>

      <Anchor id="how" />
      <PathwayStrip />
      <CandidacyBand />
      <PracticeStandards dark />

      <HairBand sand testId="hair-noticed">
        <div className="grid grid-cols-1 lg:grid-cols-11 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <HairEyebrow className="mb-3">{L.noticed.eyebrow}</HairEyebrow>
            <HairHeading>{L.noticed.heading}</HairHeading>
            <GoldRule className="mt-6" />
            <p className="text-ink/80 leading-[1.8] mt-6 text-[17px]">{L.noticed.body}</p>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <HairBookButton source="noticed" testId="noticed-book-button" />
              <button type="button" onClick={() => scrollToAnchor("stages")} data-testid="noticed-stages-link" className="text-deep-sea hover:text-gold transition-colors duration-300 text-[12px] tracking-[0.16em] uppercase font-medium">Find your stage ↓</button>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5 lg:col-start-7">
            <div className="bg-warm-white p-8 sm:p-10 relative">
              <div className="gold-frame inset-3 opacity-70" aria-hidden="true" />
              <ul className="relative space-y-4" data-testid="hair-noticed-list">
                {L.noticed.items.map((item, i) => (
                  <li key={i} className="flex gap-4 text-ink/85 text-[16px] leading-relaxed" data-testid={`hair-noticed-${i}`}>
                    <span className="font-serif italic text-gold text-xl leading-none mt-0.5">{i + 1}</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </HairBand>

      <Anchor id="clinic" />
      <HairBand testId="hair-clinic">
        <div className="grid grid-cols-1 lg:grid-cols-11 gap-12 items-start">
          <Reveal className="lg:col-span-6">
            <HairEyebrow className="mb-3">{L.clinic.eyebrow}</HairEyebrow>
            <HairHeading>{L.clinic.heading}</HairHeading>
            <GoldRule className="mt-6" />
            <p className="text-ink/80 leading-[1.8] mt-6 text-[17px] max-w-2xl" data-testid="hair-clinic-body">{L.clinic.body}</p>
            <div className="mt-8"><ClinicFacts /></div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <Photo photo={PHOTOS.clinicReception} className="aspect-[4/5] lg:aspect-[5/6]" width={1000} testId="hair-clinic-photo" />
          </Reveal>
        </div>
      </HairBand>

      <Anchor id="faq" />
      <HairBand dark narrow testId="hair-faq-band">
        <Reveal>
          <HairEyebrow className="mb-3">Questions</HairEyebrow>
          <HairHeading dark>Everything patients ask first.</HairHeading>
          <GoldRule className="mt-6" />
          <HairFaq faqs={L.faqs} dark testId="hair-landing-faq" />
          <Link to="/hair/transplantation/faq" data-testid="hair-faq-more-link" className="inline-block mt-8 text-[12px] tracking-[0.16em] uppercase font-medium text-gold hover:text-hairline transition-colors duration-300">Transplant questions, answered plainly →</Link>
        </Reveal>
      </HairBand>

      <Anchor id="book" />
      <HairBand sand testId="hair-book">
        <div className="grid grid-cols-1 lg:grid-cols-11 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <HairEyebrow className="mb-3">{L.book.eyebrow}</HairEyebrow>
            <HairHeading>{L.book.heading}</HairHeading>
            <GoldRule className="mt-6" />
            <p className="text-ink/80 leading-[1.8] mt-6 text-[17px]">{L.book.body}</p>
            <p className="standfirst text-deep-sea text-[20px] mt-6" data-testid="hair-book-offer">{OFFERS.hair}</p>
            <p className="eyebrow mt-10 mb-4">Every assessment includes</p>
            <Bullets items={L.book.includes} testId="hair-book-includes" />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <BookingPanel page="/hair" source={source} heading={L.book.widgetHeading} widgetKey="hairInPerson" practitioner={truong.name} onInteract={onInteract} onCallback={onCallback} />
          </Reveal>
        </div>
      </HairBand>

      <HairBand testId="hair-journal">
        <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
          <div>
            <HairEyebrow className="mb-3">From the journal</HairEyebrow>
            <HairHeading>Understand your hair first.</HairHeading>
          </div>
          <Link to="/hair/journal" data-testid="hair-journal-link" className="text-deep-sea hover:text-gold transition-colors duration-300 text-[12px] tracking-[0.16em] uppercase font-medium shrink-0">All hair articles</Link>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((post, i) => (
            <Reveal key={post.slug} delay={i * 0.1}>
              <Link to={`/journal/${post.slug}`} data-testid={`hair-journal-card-${post.slug}`} className="card-float block overflow-hidden group h-full">
                <Photo photo={PHOTOS[post.photo]} className="aspect-[16/10]" width={800} />
                <div className="p-7">
                  <HairEyebrow>{post.category}</HairEyebrow>
                  <h3 className="text-ink mt-2 group-hover:text-gold transition-colors duration-300">{post.title}</h3>
                  <p className="text-ink/70 text-sm mt-2 leading-relaxed">{post.excerpt}</p>
                  <p className="text-soft-grey text-xs mt-4">{post.byline}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </HairBand>

      <StickyCTA practitioner="Dr Kristy Truong" hair />
      <HairCTABand />
    </>
  );
};

export default HairLanding;
